import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAppData } from '../AppData';
import { ROLES_LIST } from '../domain/roles';
import { UserDoc } from '../domain/types';
import { errMsg } from '../domain/util';
import { normalizeEmail } from '../auth/authRules';
import { MultiSelect } from '../components/MultiSelect';

type UserForm = Partial<UserDoc> & { espacios?: string[] };

export default function Usuarios() {
  const { reload, dimensions } = useAppData();
  const [roster, setRoster] = useState<UserDoc[]>([]);
  const [espaciosByUser, setEspaciosByUser] = useState<Record<string, string[]>>({});
  const [form, setForm] = useState<UserForm>({ email: '', first_name: '', last_name: '', role: 'user', espacios: [] });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const espacioOpts = (dimensions.espacio || []).map((d) => ({ value: d.name, label: d.name }));

  const load = async () => {
    try {
      const [ros, esp] = await Promise.all([api.listAccessRoster(), api.listUserEspacios()]);
      setRoster(ros);
      setEspaciosByUser(esp);
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const email = normalizeEmail(form.email || '');
      const { espacios, ...profile } = form;
      const res = await api.adminUpsertUser({ ...profile, email });
      // Los espacios solo aplican al responsable de espacio; en cualquier otro
      // rol se limpia la asignación previa.
      await api.setUserEspacios({ email, espacios: form.role === 'space_manager' ? (espacios || []) : [] });
      setMsg(res.created
        ? `Acceso creado. ${email} ya puede ingresar con la contraseña por defecto.`
        : `Acceso actualizado para ${email}. La cuenta de login ya existía; su contraseña no se modificó.`);
      setForm({ email: '', first_name: '', last_name: '', role: 'user', espacios: [] });
      await load();
      await reload();
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  const changeRole = async (email: string, role: string) => {
    setErr(''); setMsg('');
    try {
      await api.setRole({ email, role });
      await load();
    } catch (e) { setErr(errMsg(e)); }
  };

  const remove = async (email: string) => {
    if (!confirm(`¿Quitar acceso a ${email}? Se borrará también su cuenta de login.`)) return;
    setErr(''); setMsg('');
    try {
      await api.adminRemoveUser({ email });
      setMsg(`Acceso removido para ${email}.`);
      await load();
      await reload();
    } catch (e) { setErr(errMsg(e)); }
  };

  return (
    <div className="grid">
      <h1>Usuarios</h1>
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <div className="panel grid" style={{ maxWidth: 720 }}>
        <h2>Agregar / editar acceso</h2>
        <p className="muted" style={{ margin: 0 }}>
          Al guardar se autoriza el email, se define su rol y se crea la cuenta de acceso con una
          contraseña por defecto. La persona ingresa con su email y esa contraseña; luego puede
          cambiarla desde “Olvidé mi contraseña”.
        </p>
        <div className="grid" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
          <div><label>Email</label><input type="email" autoComplete="email" value={form.email || ''} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="nombre@empresa.com" /></div>
          <div><label>Nombre</label><input value={form.first_name || ''} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} /></div>
          <div><label>Apellido</label><input value={form.last_name || ''} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} /></div>
          <div><label>Rol</label>
            <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserDoc['role'] }))}>
              {ROLES_LIST.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>
        {form.role === 'space_manager' && (
          <div style={{ maxWidth: 360 }}>
            <MultiSelect
              label="Espacios a cargo"
              options={espacioOpts}
              selected={form.espacios || []}
              onChange={(espacios) => setForm((f) => ({ ...f, espacios }))}
              allLabel="Sin espacios asignados"
            />
            <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
              El responsable solo verá revisiones y métricas de estos espacios.
            </p>
          </div>
        )}
        <div className="row"><button className="primary" disabled={busy || !form.email} onClick={save}>Guardar acceso</button></div>
      </div>

      <div className="panel">
        <table>
          <thead><tr><th>Nombre</th><th>Email</th><th>Rol</th><th>Espacios</th><th></th></tr></thead>
          <tbody>
            {roster.map((u) => (
              <tr key={u.email}>
                <td>{`${u.first_name} ${u.last_name}`.trim() || '—'}</td>
                <td>{u.email}</td>
                <td>
                  <select value={u.role} onChange={(e) => changeRole(u.email, e.target.value)} style={{ width: 160 }}>
                    {ROLES_LIST.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </td>
                <td>
                  {u.role === 'space_manager'
                    ? ((espaciosByUser[u.email.toLowerCase()] || []).join(', ') || <span className="muted">Sin asignar</span>)
                    : '—'}
                </td>
                <td>
                  <button onClick={() => setForm({ ...u, espacios: espaciosByUser[u.email.toLowerCase()] || [] })}>Editar</button>{' '}
                  <button className="danger" onClick={() => remove(u.email)}>Quitar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
